import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

interface BlogPostPreviewProps {
  title: string
  summary: string
  image: string
  date: string
  slug: string 
}

function BlogPostPreview({ title, summary, image, date, slug }: BlogPostPreviewProps) {
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
  }
  
  return (
    <div className="bg-white shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
      <div className="h-[250px] overflow-hidden">
        <img 
          src={image}
          alt={title}
          className="object-cover w-full h-full"
          loading="lazy"
        />
      </div>
      <div className="p-6 flex flex-col flex-grow">
        <p className="text-xs text-beaver-green font-medium mb-2 font-accent uppercase">
          {date}
        </p>
        <h2 className="text-xl font-bold mb-4 text-text-dark font-secondary leading-tight">
          {title}
        </h2>
        <p className="text-text-gray mb-6 font-text text-sm line-clamp-3">
          {summary}
        </p>
        <div className="mt-auto">
          <a 
            href={`/blog/${slug}`} 
            className="text-beaver-green hover:underline font-bold inline-flex items-center font-accent text-sm"
            onClick={(e) => handleNavigation(e, `/blog/${slug}`)}
          >
            READ ARTICLE
            <i className="fas fa-arrow-right ml-2 text-xs"></i>
          </a>
        </div>
      </div>
    </div>
  )
}

export default function BlogIndex() {
  // Blog post data array updated with our new local lead-generation topic
  const blogPosts = [
    {
      title: "Calgary Pergola Permit: Requirements, Cost & Guide",
      summary: "Planning a pergola in Calgary? Learn permit requirements, costs, land-use setbacks, and rules for attached pergolas and covered deck structures before building.",
      image: "/assets/images/Pergola/Modern-Cedar-Pergoal-with-2x8-cross-beams-and-1x4-Cedar-rafters.jpg",
      date: "October 14, 2026",
      slug: "calgary-pergola-permit"
    },
    {
      title: "Can Your Deck Actually Support a Hot Tub? The Structural Reality",
      summary: "No, a standard residential deck cannot safely support a filled hot tub. Learn the structural physics, framing mechanics, and building code requirements before you buy.",
      image: "/assets/images/Decks/Composite Decks/Outdoor-hot-tub-area-with-covered-lounging-space.jpg",
      date: "September 17, 2026",
      slug: "can-your-deck-support-hot-tub"
    },
    {
      title: "Affordable Fence Ideas: 37 Budget-Friendly Designs",
      summary: "Discover 37 affordable fence ideas that balance durability, curb appeal, and budget constraints. Browse our gallery of wood, vinyl, and privacy designs.",
      image: "/assets/images/Fence/Cedar-Custom-Horizontal-fence-1x6-_-1x2-pickets.jpg",
      date: "September 10, 2026",
      slug: "affordable-fence-ideas"
    },
    {
      title: "Questions to Ask Before Hiring a Calgary Deck Contractor",
      summary: "Before you hire someone to build a deck, install a fence, or complete an outdoor renovation, ask these key questions to compare Calgary contractors on more than price alone.",
      image: "/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg",
      date: "August 28, 2026",
      slug: "questions-to-ask-hiring-calgary-deck-contractor"
    },
    {
      title: "How to Remodel a Deck in Calgary",
      summary: "A complete guide to remodeling an existing deck in Calgary, including structural assessments, costs, permits, and comparing wood vs. composite decking.",
      image: "/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg",
      date: "August 10, 2026",
      slug: "remodel-deck-calgary"
    },
    {
      title: "Best Time to Build a Deck in Calgary",
      summary: "The best months to build a deck in Calgary are May through June and August through October. Learn how to beat the booking queue and plan your custom build.",
      image: "/assets/images/Decks/Composite Decks/Timbertech-Terrain-deck-in-Brown-Oak-trim-and-Sandy-Birch-surface.jpg",
      date: "July 28, 2026",
      slug: "best-time-to-build-deck-calgary"
    },
    {
      title: "Calgary Deck Maintenance Checklist: A Season-by-Season Guide",
      summary: "A season-by-season guide for maintaining wood, cedar, and composite decks in Calgary's harsh climate. Learn how to protect your deck from freeze-thaw cycles and UV exposure.",
      image: "/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg",
      date: "July 15, 2026",
      slug: "calgary-deck-maintenance-checklist"
    },
    {
      title: "TimberTech vs Trex: Which Composite Decking Is Better for Calgary Weather?",
      summary: "For Calgary's climate, TimberTech outperforms Trex. Learn how capped polymer construction holds up against Alberta winters, freeze-thaw cycles, and intense high-elevation UV exposure.",
      image: "/assets/images/Decks/Composite Decks/Azek-Vintage-Walnut.jpg",
      date: "June 23, 2026",
      slug: "timbertech-vs-trex-calgary"
    },
    {
      title: "Deck Replacement vs. Repair in Calgary: Which Is Right for You?",
      summary: "Not sure whether to repair your old deck or replace it entirely? We break down the costs, structural safety signs, and materials to help you make the best choice for your Calgary home.",
      image: "/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg",
      date: "June 4, 2026",
      slug: "deck-replacement-vs-repair-calgary"
    },
    {
      title: "Types of Decks for Your Home in Calgary: Materials & Layouts Guide",
      summary: "Discover the best types of decks for your home in Calgary. Compare composite, cedar, pressure-treated, and vinyl options built for Calgary's harsh climate.",
      image: "/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg",
      date: "May 15, 2026",
      slug: "types-of-decks-calgary"
    },
    {
      title: "FENCE INSTALLATION COSTS IN CALGARY: 2026 PRICE GUIDE",
      summary: "Discover the exact cost to build a fence in Calgary. Compare prices per linear foot for pressure-treated wood, cedar, vinyl, composite, and chain-link fencing.",
      image: "/assets/images/Fence/Cedar-Custom-Horizontal-fence-1x6-_-1x2-pickets.jpg",
      date: "May 1, 2026",
      slug: "fence-installation-costs-calgary"
    },
    {
      title: "What Is the Best Decking Material for Calgary's Harsh Winters?",
      summary: "Calgary's freeze-thaw cycles and Chinook winds destroy the wrong deck fast. A Calgary Platinum TimberTech builder compares all materials so you choose right.",
      image: "/assets/images/Decks/Composite Decks/Azek-Vintage-Walnut.jpg",
      date: "April 19, 2026",
      slug: "best-decking-material-calgary-winters"
    },
    {
      title: "HOW MUCH DOES IT COST TO BUILD A DECK IN CALGARY?",
      summary: "Discover the true cost of building a deck in Calgary. Compare prices for pressure-treated, cedar, and composite decks with real project examples.",
      image: "/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg",
      date: "August 15, 2023",
      slug: "5"
    },
    {
      title: "DO I NEED A PERMIT TO BUILD A DECK IN CALGARY?",
      summary: "Learn everything you need to know about deck building permits in Calgary, including the 600mm height rule, costs, and approval timelines.",
      image: "/assets/images/Decks/Composite Decks/Timbertech-deck-with-PT-wood-skirting-for-underdeck-storage.jpg",
      date: "July 22, 2023",
      slug: "4"
    },
    {
      title: "HOW TO UPGRADE YOUR PATIO THIS SUMMER",
      summary: "Transform your outdoor space with 6 strategic upgrades including new surfaces, shade structures, and lighting for Calgary's long summer evenings.",
      image: "/assets/images/2020/04/MG_0857.jpg",
      date: "June 5, 2023",
      slug: "3"
    },
    {
      title: "3 ADDITIONS TO TRANSFORM YOUR YARD",
      summary: "Your backyard has unlimited potential. Discover 3 additions that create privacy, comfort, and functionality for your Calgary home.",
      image: "/assets/images/2020/04/MG_0252.jpg",
      date: "May 18, 2023",
      slug: "2"
    },
    {
      title: "ADDING A DECK TO YOUR CALGARY HOME",
      summary: "A well-designed deck enhances property value and quality of life. Explore material options like TimberTech, cedar, and pressure-treated wood.",
      image: "/assets/images/2020/04/MG_0061.jpg",
      date: "April 12, 2023",
      slug: "1"
    }
  ]

  // JSON-LD Schema
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://busybeaverconstruction.com/blog"
          }
        ]
      },
      {
        "@type": "Blog",
        "@id": "https://busybeaverconstruction.com/blog#blog",
        "name": "Busy Beaver Construction Blog",
        "description": "Expert advice on deck building, fence installation, and outdoor living in Calgary.",
        "publisher": {
          "@type": "LocalBusiness",
          "@id": "https://busybeaverconstruction.com/#organization"
        },
        "itemListElement": blogPosts.map((post, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "url": `https://busybeaverconstruction.com/blog/${post.slug}`,
          "name": post.title
        }))
      }
    ]
  }
  
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  
  return (
    <main>
      <Helmet>
        <title>Blog | Busy Beaver Construction | Calgary Deck & Fence Building Experts</title>
        <meta name="description" content="Expert tips and insights on deck building, fence installation, backyard transformations, and material selection in Calgary. Over 25 years of local construction experience." />
        <meta name="keywords" content="Calgary deck blog, deck building tips, deck cost Calgary, fence installation cost Calgary, deck permits Calgary, best decking material Calgary" />
        <link rel="canonical" href="https://busybeaverconstruction.com/blog" />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      <Header />
      
      <section className="py-16 md:py-24 bg-[#f8f8f8]">
        <div className="container">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-16 text-center text-text-dark font-primary">
            Our Blog
          </h1>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {blogPosts.map((post, i) => (
              <BlogPostPreview 
                key={i}
                title={post.title}
                summary={post.summary}
                image={post.image}
                date={post.date}
                slug={post.slug}
              />
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container">
          <div className="bg-beaver-green/10 p-8 md:p-12 rounded-lg max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-text-dark font-secondary">
              Ready to Start Your Next Outdoor Project?
            </h2>
            <p className="mb-8 text-base md:text-lg text-text-gray font-text">
              Whether you're looking to build a new deck, transform your backyard, or upgrade your patio, 
              our team of experts is here to help with 25 years of experience serving Calgary.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="/appointment" 
                className="bg-beaver-green text-black px-6 py-3 uppercase text-sm rounded hover:brightness-110 transition-all duration-300 inline-block font-accent font-bold"
                onClick={(e) => handleNavigation(e, '/appointment')}
              >
                BOOK A CONSULTATION
              </a>
              <a 
                href="/estimate" 
                className="bg-black text-white px-6 py-3 uppercase text-sm rounded hover:bg-gray-800 transition-all duration-300 inline-block font-accent font-bold"
                onClick={(e) => handleNavigation(e, '/estimate')}
              >
                GET FREE ESTIMATE
              </a>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </main>
  )
}